import { useCallback, useEffect, useMemo, useState } from 'react';
import { cachedGet, putCachedRead, REFERENCE_URLS } from '../offline/offlineApi';
import { useOffline } from '../components/context/OfflineContext';
import { normalizeAddOn, normalizeAddOns, sortAddOnsByName } from '../util/addOns';

// Catalog of bookable add-ons ("Zusätze", GET /AddOn/GetAll) — one shared list for the whole app (price list,
// article popup, textblock editor, template/process pickers), same pattern as useWikis: fetched at most once per
// page load and kept in this module, offline through the reference cache (prefetched on the home page). Admin
// changes patch the list locally (no refetch) and update the offline copy as well.
//
// Lazy: nothing is requested on mount — consumers call ensureLoaded() when they need the catalog (page mount,
// popup open). After "Erneut verbinden" a list that was loaded (or failed) is fetched again once.
//
// Usage counts (templateCount, processCount, placeholderUsage) change through other admin pages (templates,
// processes, textblocks): those call invalidateAddOns() after a successful save, which only marks the list stale —
// the next load() refetches it while the old list stays on screen. The catalog on /prices additionally reloads
// once per visit for admins (changes by other admins).
let status = { list: null, loading: false, error: null };
let pending = null;     // in-flight promise
let version = 0;        // +1 per local change (a response requested before it is older than the list)
let stale = false;      // usage counts may be outdated (invalidateAddOns) — the next load() refetches
let invalidation = 0;   // +1 per invalidateAddOns() (an answer requested before it does not clear `stale`)
let seenEpoch = null;   // onlineEpoch the module last saw (reload once per reconnect)
const listeners = new Set();

function setStatus(patch) {
  status = { ...status, ...patch };
  listeners.forEach(fn => fn(status));
}

// force: fetch even if a list is loaded (reload) — the loaded list stays on screen until the answer arrives.
function load(force = false) {
  if (status.list && !force && !stale) return Promise.resolve(status.list);
  if (!pending) {
    const requestedAt = version;
    const requestedInvalidation = invalidation;
    setStatus({ loading: true, error: null });
    pending = cachedGet(REFERENCE_URLS.addOns)
      .then(data => {
        // A local change saved after this request was sent is newer than the answer: keep the list.
        if (status.list && version !== requestedAt) {
          setStatus({ loading: false });
          return status.list;
        }
        if (invalidation === requestedInvalidation) stale = false;
        const list = normalizeAddOns(data);
        setStatus({ list, loading: false, error: null });
        return list;
      })
      .catch(err => {
        // A loaded list stays on screen; only the first load reports the error.
        setStatus({ loading: false, error: status.list ? null : (err ?? new Error('load failed')) });
        throw err;
      })
      .finally(() => { pending = null; });
  }
  return pending;
}

// Home page prefetch (offline copy, offlineApi.prefetchReferenceData): runs through the shared loader, so it
// reuses the request of a mounted consumer instead of fetching in parallel.
export function preloadAddOns() {
  return load().catch(() => null);
}

// Back online / catalog page visit: load again and publish to every mounted consumer. On failure the previous
// list stays.
export function reloadAddOns() {
  return load(true).catch(() => null);
}

// After a successful save elsewhere that changes the usage of add-ons (process template create/update/duplicate/
// delete, process create/delete, PUT /EventProcess/{id}/AddOns, textblock and variant save/delete): marks the
// list stale without requesting anything — the next load() (ensureLoaded, catalog visit) refetches it.
export function invalidateAddOns() {
  invalidation += 1;
  stale = true;
}

// Local change after a successful API call: publish + keep the offline copy in line.
function commit(list) {
  version += 1;
  setStatus({ list, error: null });
  putCachedRead(REFERENCE_URLS.addOns, list);
}

// Applies a local change to the loaded list. Without a loaded list (first load still running or failed) no
// partial list is published or cached: the list is fetched again once the running request is done.
function change(fn) {
  if (!status.list) {
    version += 1;
    const running = pending ? pending.catch(() => null) : Promise.resolve();
    running.then(() => load(true)).catch(() => null);
    return;
  }
  const next = fn(status.list);
  if (next) commit(next);
}

// Created or updated catalog entry (AddOnResponse).
export function upsertAddOn(raw) {
  const addOn = normalizeAddOn(raw);
  if (!addOn) return;
  change(list => sortAddOnsByName([...list.filter(a => a.id !== addOn.id), addOn]));
}

export function removeAddOn(id) {
  const addOnId = Number(id);
  change(list => (list.some(a => a.id === addOnId) ? list.filter(a => a.id !== addOnId) : null));
}

/**
 * Add-on catalog for display and selection.
 * - addOns: all catalog entries by name — empty until loaded
 * - byId: Map id → entry
 * - loaded / loading / error (the error of the first load, null otherwise) / failed (= !!error)
 * - ensureLoaded(): starts the shared load once (no-op while loading or once loaded, unless invalidateAddOns()
 *   marked the list stale); returns the promise
 * - upsert(addOnResponse) / remove(id): local patches after admin saves (shared by every consumer)
 */
export default function useAddOns() {
  const [state, setState] = useState(status);
  const { onlineEpoch } = useOffline();

  useEffect(() => {
    let alive = true;
    const listener = (next) => { if (alive) setState(next); };
    listeners.add(listener);
    setState(status);
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  // "Erneut verbinden": the list loaded before (possibly the offline copy) is fetched again — once per reconnect,
  // however many consumers are mounted (load() shares the request).
  useEffect(() => {
    if (seenEpoch === onlineEpoch) return;
    const reconnected = seenEpoch != null && onlineEpoch > seenEpoch;
    seenEpoch = onlineEpoch;
    if (reconnected && (status.list || status.error)) reloadAddOns();
  }, [onlineEpoch]);

  const ensureLoaded = useCallback(() => load().catch(() => null), []);

  return useMemo(() => {
    const addOns = state.list ?? [];
    return {
      addOns,
      byId: new Map(addOns.map(a => [a.id, a])),
      loaded: state.list != null,
      loading: state.loading,
      error: state.error,
      failed: !!state.error,
      ensureLoaded,
      upsert: upsertAddOn,
      remove: removeAddOn,
    };
  }, [state, ensureLoaded]);
}
